import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { withRouter } from 'react-router-dom';
import Switch from 'react-switch';
import queryString from 'query-string';

import { loadProfile } from 'actions/profile';

import PageHeader from 'components/general/pageHeader';
import SortFilter from 'components/general/sortFilter';
import { Loading, SystemText, DebugInfo } from 'e4shared';
import TaskList from 'components/tasks/taskList';
import TaskCalendar from 'components/tasks/taskCalendar';
import UserMenu from 'components/user/menu';
import ManageTask from '../components/manageTask';
import { hasModule } from 'helpers/profile';

import './tasks.css';
import { getPermissions } from 'helpers/permissions';
import { setPrismMenu } from 'actions/ui';

export const UserTasks = ({ location, match, history }) => {
    const dispatch = useDispatch();
    const values = queryString.parse(location.search);

    const [filter, setFilter] = useState(values.filter || 'all');
    const [view, setView] = useState('list');
    const [showAddTask, setShowAddTask] = useState(false);
    const [lastClosed, setLastClosed] = useState();
    const [permissions, setPermissions] = React.useState(getPermissions());

    const { user, coaching, downline } = useSelector(state => state.user);
    const profile = useSelector(state => state.profile.profile);
    const error = useSelector(state => state.profile.error);

    DebugInfo('component', 'UserTasks');

    const toggleShow = () => {
        const newView = view === 'list' ? 'calendar' : 'list';
        setView(newView);
    };

    React.useEffect(() => {
        if (profile?.id && user?.id) {
            setPermissions(getPermissions(profile, user, coaching, downline));

            if (profile.id === user.id) {
                dispatch(setPrismMenu('Objectives'));
            } else {
                dispatch(setPrismMenu('MyTeam'));
            }
        }
    }, [profile?.id, user?.id]);

    useEffect(() => {
        const { id } = match.params;
        if (id) {
            dispatch(loadProfile(id));
        }
    }, [match.params.id]);

    const handleManageTaskClose = React.useCallback(() => {
        setShowAddTask(false);
        setLastClosed(new Date());
    });

    if (user && !match.params.id) {
        history.replace(match.path.replace(':id?', user.id));
    }

    const safeProfile = profile || {};

    if (!hasModule(user, 'objectives')) history.replace('/');

    if (error) {
        history.replace('/error');
    }

    const isMe = safeProfile.id === user?.id;
    const isMeSupport = isMe && user?.support;

    const btnClassName = btn => (filter === btn ? 'btn btn-sm btn-primary' : 'btn btn-sm btn-default');
    const btnAction = filter => {
        setFilter(filter);
        //// history.push(`/objectives/${safeProfile.id}?filter=${filter}`);
    };

    const btn = (filter, label, title) => (
        <button onClick={() => btnAction(filter)} className={btnClassName(filter)} title={title}>
            {label}
        </button>
    );

    const toggle = () => (
        <label className={view !== 'list' ? 'switch-on' : 'switch-off'} data-testid="toggle-calendar">
            <span>{SystemText('User/Tasks/Toggle/CalendarView')}</span>
            <Switch className="react-switch" onChange={toggleShow} checked={view !== 'list'} />
        </label>
    );

    const addNewButton = () => (
        <div className="flex">
            {permissions.objectives.canAdd && (
                <button data-testid="add-objective-button" className="btn btn-success" onClick={() => setShowAddTask(true)}>
                    <i className="fa fa-plus mr10" />
                    {SystemText('User/Tasks/Buttons/AddNew')}
                </button>
            )}
            <UserMenu profile={safeProfile} user={user} showAddTask={() => setShowAddTask(true)} hide={'objectives'} />
        </div>
    );

    const sortButtons = () => (
        <div className="btn-row">
            <div className="btn-group mr10" data-testid="usertask-filterswrapper-testid">
                {btn(
                    'all',
                    SystemText('User/Tasks/Buttons/Filters/All'),
                    isMe
                        ? SystemText('User/Tasks/Buttons/Filters/Title/IsMe/All')
                        : SystemText('User/Tasks/Buttons/Filters/Title/All', safeProfile.full_Name)
                )}
                {btn(
                    'overdue',
                    SystemText('User/Tasks/Buttons/Filters/Overdue'),
                    isMe
                        ? SystemText('User/Tasks/Buttons/Filters/Title/IsMe/Overdue')
                        : SystemText('User/Tasks/Buttons/Filters/Title/Overdue', safeProfile.full_Name)
                )}
                {btn(
                    'outstanding',
                    SystemText('User/Tasks/Buttons/Filters/Outstanding'),
                    isMe
                        ? SystemText('User/Tasks/Buttons/Filters/Title/IsMe/Outstanding')
                        : SystemText('User/Tasks/Buttons/Filters/Title/Outstanding', safeProfile.full_Name)
                )}
                {btn(
                    'awaiting',
                    SystemText('User/Tasks/Buttons/Filters/Awaiting'),
                    isMe
                        ? SystemText('User/Tasks/Buttons/Filters/Title/IsMe/Awaiting')
                        : SystemText('User/Tasks/Buttons/Filters/Title/Awaiting', safeProfile.full_Name)
                )}
                {btn(
                    'completed',
                    SystemText('User/Tasks/Buttons/Filters/Completed'),
                    isMe
                        ? SystemText('User/Tasks/Buttons/Filters/Title/IsMe/Completed')
                        : SystemText('User/Tasks/Buttons/Filters/Title/Completed', safeProfile.full_Name)
                )}
            </div>
            {isMe &&
                btn(
                    'shared',
                    SystemText('User/Tasks/Buttons/Filters/Shared'),
                    SystemText('User/Tasks/Buttons/Filters/Title/IsMeShared')
                )}
        </div>
    );

    return (
        <div>
            <PageHeader title={SystemText('User/Tasks/Header/Title')} extra={addNewButton} />
            <section className="content mtm10" data-testid="tasks-content">
                <SortFilter toggle={toggle} hideSortText sortButtons={sortButtons} />
                {profile?.id.toString() !== match.params.id || !permissions.objectives.canView ? (
                    <span data-testid="usertask-loading-testid">
                        <Loading />
                    </span>
                ) : (
                    <div className="row">
                        <div className="col-xs-12">
                            {view === 'list' ? (
                                <TaskList profileId={profile.id} filter={filter} lastRefreshed={lastClosed} />
                            ) : (
                                <TaskCalendar profileId={profile.id} filter={filter} lastRefreshed={lastClosed} />
                            )}
                        </div>
                    </div>
                )}
            </section>
            {showAddTask && (
                <ManageTask
                    key={`profile${safeProfile.id}`}
                    profileId={safeProfile.id}
                    values={{ profile_Ids: isMeSupport ? [] : [safeProfile.id] }}
                    show={showAddTask}
                    close={handleManageTaskClose}
                />
            )}
        </div>
    );
};

export default withRouter(UserTasks);
